import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RefreshCw, AlertCircle, AlertTriangle, CheckCircle2, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const STATUS = {
  valid: { label: "Em dia", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  expiring_soon: { label: "Vence em breve", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
  expired: { label: "Vencido", cls: "bg-rose-100 text-rose-700", Icon: AlertCircle },
};

export default function Recycling() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const isAdmin = ["master_admin", "company_admin"].includes(user.role);

  const refresh = async () => { const { data } = await api.get("/recycling/status"); setItems(data); };
  useEffect(() => { refresh(); }, []);

  const runJob = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recycling/run");
      toast.success(`Verificação executada: ${data.expiring} expirando, ${data.expired} vencidos`);
      refresh();
    } catch { toast.error("Erro ao executar"); }
    setBusy(false);
  };

  const totals = items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-8" data-testid="recycling-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Reciclagem & validade</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Reciclagem de certificados</h1>
          <p className="text-slate-600 mt-2 text-sm">Acompanhe a validade dos seus certificados. Recebemos avisos automaticamente 30, 14, 7 e 1 dia antes do vencimento.</p>
        </div>
        {isAdmin && (
          <Button onClick={runJob} disabled={busy} className="btn-navy" data-testid="recycling-run-btn">
            <RefreshCw className={"w-4 h-4 mr-2 " + (busy ? "animate-spin" : "")} />
            Executar verificação agora
          </Button>
        )}
      </header>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Em dia" value={totals.valid || 0} cls="bg-emerald-50 text-emerald-700" />
        <Stat label="Vencendo (≤30 dias)" value={totals.expiring_soon || 0} cls="bg-amber-50 text-amber-700" />
        <Stat label="Vencidos" value={totals.expired || 0} cls="bg-rose-50 text-rose-700" />
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-600">Você ainda não possui certificados com prazo de validade.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const S = STATUS[it.status];
            return (
              <Card key={it.id} className={"border-[var(--unigr-border)] " + (it.status === "expired" ? "border-l-4 border-l-rose-600" : it.status === "expiring_soon" ? "border-l-4 border-l-amber-500" : "")}>
                <CardContent className="p-5 grid sm:grid-cols-12 gap-3 items-center">
                  <div className="sm:col-span-5">
                    <div className="font-display font-bold">{it.course_title}</div>
                    <div className="text-xs text-slate-500 font-mono mt-1">{it.code}</div>
                  </div>
                  <div className="sm:col-span-3 text-sm">
                    <div className="text-slate-500">Emitido</div>
                    <div className="font-medium">{(it.issued_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="sm:col-span-2 text-sm">
                    <div className="text-slate-500">Vence</div>
                    <div className="font-medium">{(it.expires_at || "—").slice(0, 10)}</div>
                    {it.days_left != null && <div className="text-xs text-slate-500">{it.days_left > 0 ? `${it.days_left} dia(s)` : "expirado"}</div>}
                  </div>
                  <div className="sm:col-span-2 flex justify-end">
                    <div className="flex flex-col items-end gap-2">
                      <span className={"px-2 py-1 rounded text-xs flex items-center gap-1 " + S.cls}>
                        <S.Icon className="w-3 h-3" /> {S.label}
                      </span>
                      {it.status !== "valid" && (
                        <Link to={`/courses/${it.course_id}`}><Button size="sm" className="btn-red" data-testid={`recycle-${it.code}`}><Play className="w-3 h-3 mr-1" />Reciclar agora</Button></Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, cls }) {
  return (
    <Card><CardContent className="p-5">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={"inline-block mt-2 px-3 py-1 rounded text-sm font-bold " + cls}>{value}</div>
    </CardContent></Card>
  );
}
